import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { MessageSquareText, Mail, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePageTracking } from './hooks/useAnalytics';
import { trackWhatsAppClick, trackEmailClick, trackPhoneClick } from './utils/analytics';

// Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import CookieConsent from './components/shared/CookieConsent';

// Lazy-loaded Pages
const HomePage = lazy(() => import('./pages/HomePage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const ListingDetailPage = lazy(() => import('./pages/ListingDetailPage'));
const BookingSuccessPage = lazy(() => import('./pages/BookingSuccessPage'));
const BusinessPartnerPage = lazy(() => import('./pages/BusinessPartnerPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const SubscriptionPlansPage = lazy(() => import('./pages/SubscriptionPlansPage'));
const CorporateSubscriptionPage = lazy(() => import('./pages/CorporateSubscriptionPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const TestEmailPage = lazy(() => import('./pages/TestEmailPage'));
const OfficeImportPage = lazy(() => import('./pages/admin/OfficeImportPage'));
const CompanyPage = lazy(() => import('./pages/CompanyPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const LocationsMapPage = lazy(() => import('./pages/LocationsMapPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));

// Admin Pages
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-gray-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Yükleniyor...</p>
    </div>
  </div>
);

function App() {
  const { t } = useTranslation();
  const location = useLocation();
  const [showContactOptions, setShowContactOptions] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Track page views automatically
  try {
    usePageTracking();
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Update document title based on route
  useEffect(() => {
    const path = location.pathname;
    let title = "Listco Work - İstanbul'da Toplantı Odası, Hazır Ofis ve Ortak Çalışma Alanı Kiralama";
    
    // Extract company and service type from URL if it's a company page
    if (path.startsWith('/mekan/')) {
      const parts = path.split('/');
      const companySlug = parts[2];
      const serviceType = parts[3];
      
      // Format company name (replace hyphens with spaces and capitalize)
      const companyName = companySlug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Format service type
      let serviceLabel = '';
      if (serviceType) {
        switch (serviceType) {
          case 'toplanti-odasi':
            serviceLabel = 'Toplantı Odaları';
            break;
          case 'hazir-ofis':
            serviceLabel = 'Hazır Ofisler';
            break;
          case 'ortak-calisma':
            serviceLabel = 'Ortak Çalışma Alanları';
            break;
          case 'sanal-ofis':
            serviceLabel = 'Sanal Ofisler';
            break;
        }
        title = `${companyName} ${serviceLabel} - Listco Work`;
      } else {
        title = `${companyName} - Çalışma Alanları ve Ofisler | Listco Work`;
      }
      
      // If there's a location hash, add it to the title
      if (location.hash) {
        const locationName = decodeURIComponent(location.hash.substring(1));
        title = `${companyName} ${serviceLabel} ${locationName} - Listco Work`;
      }
    }
    
    // Admin pages
    if (path.startsWith('/admin')) {
      title = "Admin Panel - Listco Work";
    }
    
    document.title = title;
  }, [location]);


  // Initialize AdSense with error handling
  useEffect(() => {
    try {
      const isProduction = window.location.hostname !== 'localhost' && 
                           !window.location.hostname.includes('webcontainer') &&
                           !window.location.hostname.includes('stackblitz');
      
      if (isProduction && window.adsbygoogle) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      console.warn('AdSense initialization error:', e);
    }
  }, [location.pathname]);

  const contactOptions = [
    {
      icon: MessageSquareText,
      label: 'WhatsApp',
      action: () => {
        try {
          trackWhatsAppClick('floating_button');
          window.open('https://wa.me/905345177040', '_blank');
        } catch (error) {
          console.warn('WhatsApp action error:', error);
        }
      }
    },
    {
      icon: Mail,
      label: 'E-posta',
      action: () => {
        try {
          trackEmailClick('floating_button');
          window.location.href = 'mailto:info@listco.work';
        } catch (error) {
          console.warn('Email action error:', error);
        }
      }
    },
    {
      icon: Phone,
      label: 'Ara',
      action: () => {
        try {
          trackPhoneClick('floating_button');
          window.location.href = 'tel:+905345177040';
        } catch (error) {
          console.warn('Phone action error:', error);
        }
      }
    }
  ];

  // Check if current route is admin route
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Add error boundary for the entire app
  const [hasError, setHasError] = useState(false);
  
  useEffect(() => {
    const handleError = (event) => {
      console.error('Global error:', event.error);
      setHasError(true);
    };
    
    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  
  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Listco Work</h1>
          <p className="text-gray-600 mb-6">Sayfa yüklenirken bir hata oluştu.</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Sayfayı Yenile
          </button>
          <p className="mt-4 text-sm text-gray-500">
            Sorun devam ederse: <a href="tel:+905348266570" className="text-primary-600">+90 534 826 6570</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {!isAdminRoute && <Header />}
      <main className="flex-grow">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/arama" element={<SearchPage />} />
            <Route path="/mekan/:id" element={<ListingDetailPage />} />
            <Route path="/mekan/:companySlug" element={<CompanyPage />} />
            <Route path="/mekan/:companySlug/:serviceType" element={<CompanyPage />} />
            <Route path="/rezervasyon-basarili" element={<BookingSuccessPage />} />
            <Route path="/is-ortaklari" element={<BusinessPartnerPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPage />} />
            <Route path="/abonelik-planlari" element={<SubscriptionPlansPage />} />
            <Route path="/kurumsal-abonelik" element={<CorporateSubscriptionPage />} />
            <Route path="/odeme" element={<PaymentPage />} />
            <Route path="/odeme-basarili" element={<PaymentSuccessPage />} />
            <Route path="/test-email" element={<TestEmailPage />} />
            <Route path="/admin/ofis-import" element={<OfficeImportPage />} />
            <Route path="/lokasyonlar" element={<LocationsMapPage />} />
            <Route path="/gizlilik-politikasi" element={<PrivacyPolicyPage />} />
            <Route path="/kullanim-kosullari" element={<TermsOfServicePage />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard/*" element={<AdminDashboard />} />
            
            {/* Legacy URL redirects for SEO */}
            <Route path="/search" element={<Navigate to="/arama" replace />} />
            <Route path="/listing/:id" element={<Navigate to={location => `/mekan/${location.pathname.split('/')[2]}`} replace />} />
            <Route path="/booking-success" element={<Navigate to="/rezervasyon-basarili" replace />} />
            <Route path="/for-business" element={<Navigate to="/is-ortaklari" replace />} />
            <Route path="/subscription-plans" element={<Navigate to="/abonelik-planlari" replace />} />
            <Route path="/corporate-subscription" element={<Navigate to="/kurumsal-abonelik" replace />} />
            <Route path="/payment" element={<Navigate to="/odeme" replace />} />
            <Route path="/payment-success" element={<Navigate to="/odeme-basarili" replace />} />
            <Route path="/locations" element={<Navigate to="/lokasyonlar" replace />} />
            
            {/* Admin URL redirects */}
            <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
            
            {/* 404 Page */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      {!isAdminRoute && <Footer />}
      
      {!isAdminRoute && (
        <div className="fixed bottom-6 right-6 z-50">
          {showContactOptions && (
            <div className="flex flex-col gap-2 mb-4 fade-in">
              {contactOptions.map(({ icon: Icon, label, action }) => (
                <button
                  key={label}
                  onClick={action}
                  className="bg-white text-primary-600 p-4 rounded-full shadow-lg flex items-center hover:bg-primary-50 hover:border-primary-300 transition-colors"
                  aria-label={label}
                >
                  <Icon size={24} />
                  <span className="ml-2 hidden md:inline">{label}</span>
                </button>
              ))}
            </div>
          )}
          
          <button 
            onClick={() => setShowContactOptions(!showContactOptions)}
            className="bg-primary-600 text-white p-4 rounded-full shadow-lg flex items-center hover:bg-primary-700 transition-colors"
            aria-label={t('contactUs')}
          >
            <MessageSquareText size={24} />
            <span className="ml-2 hidden md:inline">{t('contactUs')}</span>
          </button>
        </div>
      )}

      {/* Cookie Consent */}
      <CookieConsent />
    </div>
  );
}

export default App;